/**
 * Team management (owner-only): the org member roster + invite administration.
 *
 *   - Roster: GET /members → a table of each member's wallet + the roles they
 *     hold in this org.
 *   - Mint: a form that POSTs /invites (role select, maxUses, expiry, optional
 *     label). On success the one-time `code` is shown ONCE, alongside a
 *     ready-to-share `${origin}${BASE_URL}?invite=<code>` URL (the SPA lives
 *     under /dashboard/, so the link must carry that base — the root serves the
 *     landing page) consistent with the redeem flow (auth.tsx captures
 *     `?invite=` on boot).
 *   - Invites: GET /invites → outstanding invites (role / uses / expiry) each
 *     with a Revoke button (POST /invites/{id}/revoke), listed below the mint
 *     form so a freshly minted invite appears at the bottom of the list.
 *
 * Styling is the shared console design system only — surfaces (.card), section
 * titles (.section-title), tables (.detail-table), controls (.input), buttons
 * (.btn / .btn-save / .btn-pool-remove), and notes/errors (.note / .error), all
 * built on Saga tokens. The handful of `team-*` classes are layout-only (see the
 * "Team tab" block in index.css); the tab invents no visual design of its own.
 *
 * The tab is owner-gated at the App level (only listed/routable for owners); it
 * also guards internally as belt-and-suspenders, and gates its data fetches on
 * `isOwner` so a non-owner never issues the owner-only reads.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import {
  useMembers,
  useInvites,
  useMintInvite,
  useRevokeInvite,
  type Invite,
  type Member,
} from '../lib/queries';
import { ApiError } from '../lib/api';

/** The three assignable member tiers (role IDs are resolved server-side from the
 *  selected tier; the form sends the human-readable role and the handler maps
 *  it). Default selection is the least-privileged tier, `viewer`. */
const ROLES = ['viewer', 'trader', 'owner'] as const;
type Role = (typeof ROLES)[number];

const DEFAULT_EXPIRY_DAYS = 7;

/** Truncate a wallet address to `0x1234…ABCD` for compact table display; the
 *  full value lives in the row's `title` for hover/copy. */
function truncateWallet(addr: string): string {
  if (addr.length <= 13) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/** An ISO 8601 timestamp `days` days from now — the default invite expiry. */
function expiryFromNow(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

function errMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'unknown error';
}

function MembersRoster({ enabled }: { enabled: boolean }) {
  const { data, isLoading, isError, error } = useMembers(enabled);

  if (isLoading) return <p className="note">Loading members…</p>;
  if (isError) {
    return (
      <p className="error" role="alert">
        Failed to load members: {errMessage(error)}
      </p>
    );
  }

  const members: Member[] = data?.members ?? [];
  if (members.length === 0) {
    return <p className="note">No members yet.</p>;
  }

  return (
    <table className="detail-table">
      <thead>
        <tr>
          <th>Wallet</th>
          <th>Roles</th>
        </tr>
      </thead>
      <tbody>
        {members.map((m) => (
          <tr key={m.identityId}>
            <td title={m.walletAddress}>{truncateWallet(m.walletAddress)}</td>
            <td>{m.roles.length > 0 ? m.roles.join(', ') : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvitesList({ enabled }: { enabled: boolean }) {
  const { data, isLoading, isError, error } = useInvites(enabled);
  const revoke = useRevokeInvite();

  if (isLoading) return <p className="note">Loading invites…</p>;
  if (isError) {
    return (
      <p className="error" role="alert">
        Failed to load invites: {errMessage(error)}
      </p>
    );
  }

  // Guardius keeps revoked invites in the list with `status:'revoked'`. They're
  // dead — drop them so the "outstanding" table only shows live invites. Without
  // this, a successful (idempotent) revoke leaves the row sitting there looking
  // active, which reads as "revoke does nothing".
  const invites: Invite[] = (data?.invites ?? []).filter((inv) => inv.status !== 'revoked');
  if (invites.length === 0) {
    return <p className="note">No outstanding invites.</p>;
  }

  return (
    <>
      {revoke.isError && (
        <p className="error" role="alert">
          Failed to revoke invite: {errMessage(revoke.error)}
        </p>
      )}
      <table className="detail-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Uses</th>
            <th>Expires</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {invites.map((inv) => {
            const role = inv.roleIds?.join(', ') ?? '—';
            const used = inv.useCount ?? 0;
            const max = inv.maxUses ?? '∞';
            // Guardius `expiresAt` is epoch SECONDS.
            const expires =
              typeof inv.expiresAt === 'number'
                ? new Date(inv.expiresAt * 1000).toISOString()
                : '—';
            return (
              <tr key={inv.inviteId} data-invite-id={inv.inviteId}>
                <td title={inv.label}>{role}</td>
                <td>
                  {used} / {max}
                </td>
                <td>{expires}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-pool-remove"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(inv.inviteId)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function MintForm() {
  const mint = useMintInvite();
  const [role, setRole] = useState<Role>('viewer');
  const [maxUses, setMaxUses] = useState(1);
  // Expiry as an ISO string; default 7 days out. Kept as ISO so the payload is
  // exactly what we render; the input is a plain text field for the timestamp.
  const [expiresAt, setExpiresAt] = useState(() => expiryFromNow(DEFAULT_EXPIRY_DAYS));
  const [label, setLabel] = useState('');
  // Which field was last copied to the clipboard ('link' | 'code'), for the
  // "Copied!" confirmation; null = nothing copied (or the copy failed).
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mint.isPending) return;
    mint.mutate({
      role,
      maxUses,
      expiresAt,
      ...(label.trim() ? { label: label.trim() } : {}),
    });
  };

  const minted = mint.data;
  // The console SPA (which redeems the invite) is served under the build base
  // path (/dashboard/), NOT the site root — `/` serves the static landing page,
  // which has no invite logic. Build the link against BASE_URL so the invitee
  // lands on the sign-in flow, not marketing.
  const shareUrl = minted
    ? `${window.location.origin}${import.meta.env.BASE_URL}?invite=${minted.code}`
    : '';

  // A fresh mint resets any prior "Copied!" confirmation.
  useEffect(() => {
    setCopied(null);
    setCopyFailed(false);
  }, [minted?.code]);

  // Copy the link/code to the clipboard. The raw value stays visible above as a
  // manual fallback, so a clipboard failure (e.g. insecure context) is surfaced,
  // not swallowed — the user can still select the text by hand.
  const copy = async (which: 'link' | 'code', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFailed(false);
      setCopied(which);
    } catch {
      setCopied(null);
      setCopyFailed(true);
    }
  };

  return (
    <form className="team-mint-form" onSubmit={onSubmit}>
      <label className="team-field">
        <span>Role</span>
        <select
          className="input"
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          aria-label="Role"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>

      <label className="team-field">
        <span>Max uses</span>
        <input
          className="input"
          type="number"
          min={1}
          value={maxUses}
          onChange={(e) => setMaxUses(Math.max(1, Number(e.target.value) || 1))}
          aria-label="Max uses"
        />
      </label>

      <label className="team-field">
        <span>Expires at (ISO 8601)</span>
        <input
          className="input"
          type="text"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          aria-label="Expires at"
        />
      </label>

      <label className="team-field">
        <span>Label (optional)</span>
        <input
          className="input"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          aria-label="Label"
        />
      </label>

      <button type="submit" className="btn btn-save team-mint-submit" disabled={mint.isPending}>
        {mint.isPending ? 'Minting…' : 'Mint invite'}
      </button>

      {mint.isError && (
        <p className="error" role="alert">
          Failed to mint invite: {errMessage(mint.error)}
        </p>
      )}

      {minted && (
        <div className="team-mint-result" role="status">
          <p className="note">
            This code is shown once — copy it now. It won&apos;t be shown again.
          </p>
          <p className="team-mint-code">
            <span className="team-mint-label">Code</span>
            <code data-testid="mint-code">{minted.code}</code>
          </p>
          <p className="team-mint-link">
            <span className="team-mint-label">Share link</span>
            <a href={shareUrl} data-testid="mint-share-url">
              {shareUrl}
            </a>
          </p>
          <div className="team-mint-actions">
            <button type="button" className="btn" onClick={() => copy('link', shareUrl)}>
              {copied === 'link' ? 'Copied!' : 'Copy link'}
            </button>
            <button type="button" className="btn" onClick={() => copy('code', minted.code)}>
              {copied === 'code' ? 'Copied!' : 'Copy code'}
            </button>
          </div>
          {copyFailed && (
            <p className="error" role="alert">
              Couldn&apos;t copy to the clipboard — select the link above to copy it manually.
            </p>
          )}
        </div>
      )}
    </form>
  );
}

export default function TeamTab() {
  const { isOwner } = useAuth();

  // Belt-and-suspenders: the App only lists/routes this tab for owners, but a
  // non-owner who reaches it must see nothing actionable and issue no reads.
  if (!isOwner) {
    return (
      <p className="note" role="alert">
        Team management is available to org owners only.
      </p>
    );
  }

  return (
    <div className="team-tab">
      <section className="card">
        <h3 className="section-title">Members</h3>
        <MembersRoster enabled={isOwner} />
      </section>

      <section className="card">
        <h3 className="section-title">Invite someone</h3>
        <MintForm />
      </section>

      <section className="card">
        <h3 className="section-title">Outstanding invites</h3>
        <InvitesList enabled={isOwner} />
      </section>
    </div>
  );
}
